import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Globe, Check, ChevronDown } from "lucide-react";

const LanguageSelector = () => {
  const { language, availableLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = availableLanguages.find((l) => l.code === language) || availableLanguages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = (newLangCode) => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    const supportedLangs = ["en", "en-au", "en-nz", "ar", "zh-cn"];
    
    if (pathParts.length > 0 && supportedLangs.includes(pathParts[0].toLowerCase())) {
      pathParts[0] = newLangCode;
    } else {
      pathParts.unshift(newLangCode);
    }
    
    const newPath = "/" + pathParts.join("/");
    navigate(newPath + location.search + location.hash);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold text-wool-charcoal bg-white hover:bg-wool-sand/60 border border-wool-border rounded-lg transition shadow-sm focus:outline-none focus:ring-2 focus:ring-wool-bronze/30 shrink-0"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Globe className="w-3.5 h-3.5 text-wool-bronze shrink-0" />
        <span className="hidden sm:inline whitespace-nowrap">{currentLang.name}</span>
        <span className="sm:hidden text-[11px] font-bold uppercase whitespace-nowrap">
          {currentLang.code.split("-")[0]}
        </span>
        <ChevronDown className="w-3 h-3 text-wool-muted shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute end-0 mt-1.5 w-44 bg-white border border-wool-border rounded-xl shadow-premium py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100">
          <div className="px-3 py-1 text-[11px] font-semibold text-wool-muted uppercase tracking-wider border-b border-wool-border/50">
            Select Language
          </div>
          {availableLanguages.map((lang) => {
            const isSelected = lang.code === language;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium transition ${
                  isSelected
                    ? "bg-wool-sand/50 text-wool-bronze-dark font-semibold"
                    : "text-wool-charcoal hover:bg-wool-bg"
                }`}
              >
                <span>{lang.name}</span>
                {isSelected && <Check className="w-3.5 h-3.5 text-wool-bronze" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
